//@ts-nocheck
import DeleteModal from '../../Trip/DeleteModal/DeleteModal';
import { DataGrid } from '@mui/x-data-grid';
import { CircularProgress, Box, Pagination } from '@mui/material';
import { tripsColumns } from './TripsListColumns';
import { useNavigate } from 'react-router';
import { XGButton } from 'xg-cl';
import { Copy, Trash } from '@phosphor-icons/react';
import { cloneTrip, deleteTrip } from '../../../services/trip/trip';
import { ErrorToast } from '../../../utils/alerts';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import * as PropTypes from 'prop-types';
import './TripsList.css';

const TripsList = ({
  trips,
  totalRecordsCount,
  onPaginationChange,
  currentPage,
  onRefresh,
  onSortChange,
  sortModel,
  loading = false
}) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(null);

  const handleSortModelChange = (newSortModel) => {
    onSortChange?.(newSortModel);
  };

  const handleClickCopyTrip = async (id) => {
    setIsCopying(true);
    setSelectedTripId(id);
    try {
      const { data } = await cloneTrip(id);
      navigate(`/trips/${data?.newtripid}`);
    } catch (error) {
      ErrorToast(t('errors:tripCouldNotBeCopied'));
    } finally {
      setIsCopying(false);
    }
  };

  const handleDeleteTripClick = async (id) => {
    setSelectedTripId(id);
    setIsDeleteModalOpen(true);
  };

  const handleOnConfirmRemoval = async () => {
    try {
      setIsDeleting(true);
      await deleteTrip(selectedTripId);
      onRefresh(1);
    } catch (error) {
      ErrorToast(t('errors:tripCouldNotBeDeleted', { reason: error.message }));
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleOnCancelRemoval = () => {
    setIsDeleteModalOpen(false);
  };

  const updatedColumns = [
    ...tripsColumns,
    {
      field: 'deleteTrip',
      headerClassName: 'trips-list__data-grid-header',
      headerName: '',
      sortable: false,
      width: 80,
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      maxWidth: 100,
      renderCell: (params) => {
        return (
          <XGButton
            data-testid='delete-trip-button'
            id={`trips-list-delete-button-${params?.id}`}
            text={t('common:delete')}
            size='small'
            variant='text'
            type='primary'
            startIcon={<Trash size={12} />}
            onClick={() => handleDeleteTripClick(params?.id)}
            disabled={isDeleting || isCopying}
          />
        );
      }
    },
    {
      field: 'cloneTrips',
      headerClassName: 'trips-list__data-grid-header',
      headerName: '',
      sortable: false,
      width: 200,
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      maxWidth: 100,
      renderCell: (params) => {
        return (
          <Box sx={{ position: 'relative' }}>
            <XGButton
              data-testid='clone-trip-button'
              id={`trips-list-clone-button-${params?.id}`}
              text={t('common:copy')}
              size='small'
              variant='text'
              type='primary'
              startIcon={<Copy size={12} />}
              onClick={() => handleClickCopyTrip(params?.id)}
              disabled={isDeleting || isCopying}
            />
            {isCopying && selectedTripId === params?.id && (
              <CircularProgress
                id='copy-trip-spinner'
                className='spinner spinner__primary spinner--small'
                size={22}
              />
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <div style={{ height: '100%', minWidth: '850px' }}>
        <DataGrid
          className='trips-list__footer'
          sx={{
            '.MuiDataGrid-footerContainer': {
              justifyContent: 'center'
            }
          }}
          rows={trips}
          columns={updatedColumns}
          loading={loading}
          sortingMode='server'
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          disableMultipleColumnsSorting={true}
          sortingOrder={['asc', 'desc']}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false
              }
            },
            pagination: {
              paginationModel: {
                pageSize: 10,
                page: 0
              }
            }
          }}
          slots={{
            pagination: (props) => (
              <Pagination
                count={Math.ceil(totalRecordsCount / 10)}
                siblingCount={0}
                boundaryCount={1}
                onChange={onPaginationChange}
                size='large'
                page={currentPage}
                showFirstButton
                showLastButton
                variant='string'
                disabled={loading}
                {...props}
              />
            )
          }}
          disableRowSelectionOnClick
          disableColumnMenu={true}
          columnHeaderHeight={44}
          paginationMode='server'
          filterMode='server'
          rowCount={Math.ceil(totalRecordsCount / 10)}
        />
      </div>

      <DeleteModal
        id='delete-amenity-modal'
        isOpenModal={isDeleteModalOpen}
        title={t('trip:deleteTrip')}
        onConfirm={handleOnConfirmRemoval}
        onCancel={handleOnCancelRemoval}
        description={`${t('trip:deleteTripConfirmation')} ${t(
          'common:thisActionCannotBeUndone'
        )}`}
        isSubmitting={isDeleting}
      />
    </div>
  );
};

TripsList.propTypes = {
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      destination: PropTypes.string,
      startDate: PropTypes.string,
      lastModified: PropTypes.string
    })
  ),
  totalRecordsCount: PropTypes.number,
  onPaginationChange: PropTypes.func,
  currentPage: PropTypes.number,
  onRefresh: PropTypes.func,
  onSortChange: PropTypes.func.isRequired,
  sortModel: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool
};

export default TripsList;
